import {
  DoneAll,
  EmailOutlined,
  InfoOutlined,
  CheckCircleOutline,
  WarningAmberOutlined,
  AccessTime,
  Delete,
  Check,
  NotificationsOutlined,
} from '@mui/icons-material';
import { Button, SearchBar, CardContainer, Tabs } from 'finara-components';
import { useState } from 'react';

interface Tab {
  id: string;
  title: string;
  onSelect: () => void;
}

import { ErrorState, LoadingState } from '@/components/common';
import { Navbar } from '@/components/super-admin/general/Navbar';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
} from '@/hooks/queries/useNotifications';
import { useAuth } from '@/hooks/useAuth';
import { useEnrichedNotifications } from '@/hooks/useEnrichedNotifications';
import { showErrorToast, showSuccessToast, showInfoToast } from '@/utils/toast';

export const NotificationsCenter: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('todas');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  // Determine which nav items to use based on user role
  const navItems = user?.role === 'super_admin' ? superAdminNavItems : adminNavItems;

  // Build query based on filters
  const notificationsQuery = {
    page: currentPage,
    limit,
    orderDirection: 'DESC' as const,
    type: 'in-app' as const,
    ...(activeTab === 'sin-leer' && { isRead: false }),
    ...(activeTab === 'leidas' && { isRead: true }),
  };

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useNotifications(notificationsQuery);
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();
  const deleteNotificationMutation = useDeleteNotification();

  const allNotifications = notificationsData?.data || [];

  // Enrich notifications with user and organization data
  const { enrichedNotifications: allEnrichedNotifications, isLoading: isEnriching } =
    useEnrichedNotifications(allNotifications);

  // Filter locally by search term and category
  const filteredNotifications = allEnrichedNotifications.filter(notification => {
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        notification.title.toLowerCase().includes(searchLower) ||
        notification.message.toLowerCase().includes(searchLower) ||
        notification.user?.name.toLowerCase().includes(searchLower) ||
        notification.organization?.name.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    return true;
  });

  const stats = {
    unread: notificationsData?.data.filter(n => !n.isRead).length || 0,
    total: notificationsData?.total || 0,
  };

  // Define tabs
  const tabs: Tab[] = [
    {
      id: 'todas',
      title: `Todas (${notificationsData?.total || 0})`,
      onSelect: () => {
        setActiveTab('todas');
        setCurrentPage(1);
      },
    },
    {
      id: 'sin-leer',
      title: `Sin leer (${stats.unread})`,
      onSelect: () => {
        setActiveTab('sin-leer');
        setCurrentPage(1);
      },
    },
    {
      id: 'leidas',
      title: `Leídas (${notificationsData?.data.filter(n => n.isRead).length || 0})`,
      onSelect: () => {
        setActiveTab('leidas');
        setCurrentPage(1);
      },
    },
  ];

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'in-app':
        return <InfoOutlined className="text-blue-500" />;
      case 'email':
        return <CheckCircleOutline className="text-green-500" />;
      case 'push':
        return <WarningAmberOutlined className="text-yellow-500" />;
      default:
        return <InfoOutlined className="text-blue-500" />;
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Justo ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return `Hace ${diffDays}d`;
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.id));
    }
  };

  const handleSelectNotification = (id: string) => {
    setSelectedNotifications(prev =>
      prev.includes(id) ? prev.filter(nId => nId !== id) : [...prev, id]
    );
  };

  const handleDeleteNotification = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteNotificationMutation.mutateAsync(id);
      setSelectedNotifications(prev => prev.filter(nId => nId !== id));
      showSuccessToast('Notificación eliminada correctamente');
    } catch (_error) {
      showErrorToast('Error al eliminar la notificación');
    }
  };

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await markAsReadMutation.mutateAsync({
        notificationId: id,
        data: { isRead: true },
      });
      showSuccessToast('Notificación marcada como leída');
    } catch (_error) {
      showErrorToast('Error al marcar la notificación como leída');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const result = await markAllAsReadMutation.mutateAsync();
      if (result.affected > 0) {
        showSuccessToast(`${result.affected} notificaciones marcadas como leídas`);
      } else {
        showInfoToast('No hay notificaciones sin leer');
      }
    } catch (_error) {
      showErrorToast('Error al marcar todas las notificaciones como leídas');
    }
  };

  const handleCardClick = (id: string) => {
    handleSelectNotification(id);
  };

  return (
    <div className="min-h-screen">
      <Navbar navItems={navItems} userName={user?.name || 'Usuario'} activeItem="Notificaciones" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Centro de Notificaciones</h1>
            <p className="text-gray-600 mt-1">
              Gestiona todas las notificaciones y alertas del sistema
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="white"
              startIcon={<DoneAll />}
              size="small"
              onClick={() => void handleMarkAllAsRead()}
              disabled={markAllAsReadMutation.isPending || stats.unread === 0}
            >
              Marcar todo como leído
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:!grid-cols-3 gap-6 mb-6">
          <CardContainer>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Notificaciones sin leer</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.unread}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <EmailOutlined className="text-gray-600" />
                </div>
              </div>
            </div>
          </CardContainer>

          <CardContainer>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <NotificationsOutlined className="text-blue-600" />
                </div>
              </div>
            </div>
          </CardContainer>
        </div>

        {/* Main Content */}
        <CardContainer>
          {/* Filters */}
          <div className="p-6 ">
            <div className="flex items-center justify-between gap-4">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Buscar notificaciones..."
                className="flex-1 "
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6">
            <Tabs tabs={tabs} activeTabId={activeTab} />
          </div>

          {/* Notifications List */}
          <div className="p-6">
            {isLoading || isEnriching ? (
              <LoadingState message="Cargando notificaciones..." />
            ) : error ? (
              <ErrorState
                title="Error al cargar notificaciones"
                message="No se pudieron cargar las notificaciones. Por favor, intenta nuevamente."
                error={error}
                showRetry
                onRetry={() => void refetch()}
              />
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Notificaciones</h3>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-gray-600 hover:text-gray-900"
                    disabled={filteredNotifications.length === 0}
                  >
                    Seleccionar todo
                  </button>
                </div>

                <div className="space-y-3">
                  {filteredNotifications.map(notification => (
                    <div
                      key={notification.id}
                      onClick={() => handleCardClick(notification.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCardClick(notification.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className={`w-full border rounded-lg p-4 transition-colors cursor-pointer ${
                        !notification.isRead
                          ? 'bg-gray-50 border-gray-300'
                          : 'bg-white border-gray-200'
                      } ${selectedNotifications.includes(notification.id) ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.includes(notification.id)}
                          onChange={() => {}}
                          onClick={e => {
                            e.stopPropagation();
                            handleSelectNotification(notification.id);
                          }}
                          className="z-10 mt-1 h-4 w-4 text-black bg-black focus:ring-black border-gray-300 rounded cursor-pointer"
                        />
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                              {notification.title}
                              {!notification.isRead && (
                                <span className="w-2 h-2 bg-black rounded-full"></span>
                              )}
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                          {/* User and Organization Info */}
                          {(notification.user || notification.organization) && (
                            <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                              {notification.user && (
                                <span className="flex items-center px-2 py-0.5 bg-blue-50 rounded text-blue-700">
                                  <span className="font-medium">{notification.user.name}</span>
                                </span>
                              )}
                              {notification.organization && (
                                <span className="px-2 py-0.5 bg-purple-50 rounded text-purple-700">
                                  {notification.organization.name}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <AccessTime fontSize="inherit" />
                                {formatTimeAgo(notification.createdAt)}
                              </div>
                              {typeof notification.data?.['category'] === 'string' && (
                                <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-700">
                                  {notification.data['category']}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {!notification.isRead && (
                                <button
                                  onClick={e => void handleMarkAsRead(notification.id, e)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                  title="Marcar como leído"
                                  disabled={markAsReadMutation.isPending}
                                >
                                  <Check fontSize="small" />
                                </button>
                              )}
                              <button
                                onClick={e => void handleDeleteNotification(notification.id, e)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Eliminar"
                                disabled={deleteNotificationMutation.isPending}
                              >
                                <Delete fontSize="small" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {filteredNotifications.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <NotificationsOutlined
                        className="text-gray-400"
                        style={{ fontSize: '2rem' }}
                      />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No se encontraron notificaciones
                    </h3>
                    <p className="text-gray-500">Ajusta los filtros para ver más resultados.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContainer>
      </div>
    </div>
  );
};
